import React from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";

const Step7_Confirmation = ({ setActiveStep }) => {
  const { watch } = useFormContext();
  const formData = watch();

  const sections = [
    {
      title: "Basic Info",
      stepIndex: 0,
      fields: [
        { label: "Loan Type", value: formData.loan_type },
        { label: "Loan Name", value: formData.loan_name },
        { label: "Currency", value: formData.currency },
        { label: "Loan Duration", value: formData.loan_duration },
      ],
    },
    {
      title: "Loan Amounts",
      stepIndex: 1,
      fields: [
        { label: "Duration to Qualify", value: formData.duration_to_qualify },
        { label: "Max Loan Amount", value: formData.maximum_loan_amount },
        { label: "Min Loan Amount", value: formData.minimum_loan_amount },
        {
          label: "Min Installment Count",
          value: formData.minimum_instalment_count,
        },
        { label: "Max Installment Count", value: formData.instalment_counts },
      ],
    },
    {
      title: "Interest & Fees",
      stepIndex: 2,
      fields: [
        { label: "Interest Type", value: formData.interest_type },
        {
          label: "Interest Frequency",
          value: formData.loan_interest_frequency,
        },
        { label: "Interest Rate", value: `${formData.loan_interest_rate}%` },
        { label: "Upfront", value: formData.upfront },
        { label: "Application Fee", value: `${formData.application_fee_p}%` },
        { label: "Insurance Fee", value: formData.insurance_fee },
        ...(formData.insurance_fee === "YES"
          ? [{ label: "Insurance %", value: `${formData.insurance_p}%` }]
          : []),
        {
          label: "Grace Period Interest",
          value: formData.grace_period_interest,
        },
      ],
    },
    {
      title: "Guarantors & Savings",
      stepIndex: 3,
      fields: [
        { label: "Guarantorship", value: formData.guarantorship },
        ...(formData.guarantorship === "YES"
          ? [
              {
                label: "Max Guarantor %",
                value: `${formData.maximum_guarantor_percentage}%`,
              },
              {
                label: "Max Saving %",
                value: `${formData.maximum_saving_percentage}%`,
              },
              {
                label: "Allow Multiplier",
                value: formData.allow_multiplier_times,
              },
              ...(formData.allow_multiplier_times === "YES"
                ? [
                    {
                      label: "Multiplier Times",
                      value: formData.multiplier_times,
                    },
                  ]
                : []),
            ]
          : [
              {
                label: "Min Saving Amount",
                value: formData.minimum_saving_amount,
              },
              {
                label: "Upto Saving %",
                value: `${formData.upto_saving_percentage}%`,
              },
            ]),
      ],
    },
    {
      title: "Repayment & Recovery",
      stepIndex: 4,
      fields: [
        { label: "Early Repayment", value: formData.early_repayment },
        { label: "Early Repayment Plan", value: formData.early_repayment_plan },
        { label: "Debt Collection", value: formData.debt_collection },
        ...(formData.debt_collection === "YES"
          ? [
              {
                label: "Debt Collection Fee %",
                value: `${formData.debt_collection_fee_p}%`,
              },
              {
                label: "Days to Debt Collection",
                value: formData.days_to_debt_collection,
              },
              {
                label: "Debt Collection Type",
                value: formData.debt_collection_type,
              },
            ]
          : []),
        { label: "Recovery", value: formData.recovery },
        ...(formData.recovery === "before_loan_end_date"
          ? [
              {
                label: "Installment Before Recovery",
                value: formData.instalment_before_recovery,
              },
            ]
          : []),
        { label: "Allow Rollover", value: formData.allow_rollover },
        ...(formData.allow_rollover === "YES"
          ? [
              { label: "Rollover Days", value: formData.rollover_days },
              {
                label: "Rollover Frequency",
                value: formData.rollover_frequency,
              },
              {
                label: "Rollover Interest Rate",
                value: formData.rollover_interest_rate,
              },
              {
                label: "Grace Period Before Rollover",
                value: formData.grace_period_b_rollover,
              },
            ]
          : []),
      ],
    },
    {
      title: "Other Configuration",
      stepIndex: 5,
      fields: [
        { label: "Notification Frequency", value: formData.notification_freq },
        { label: "Loan Agent", value: formData.loan_agent },
        ...(formData.loan_agent === "YES"
          ? [
              {
                label: "Agent Commission %",
                value: `${formData.agent_commission_p}%`,
              },
              {
                label: "Rev Commission %",
                value: `${formData.rev_commission_p}%`,
              },
            ]
          : []),
        { label: "Top Up", value: formData.top_up },
        ...(formData.top_up === "YES"
          ? [{ label: "Top Up %", value: `${formData.top_up_p}%` }]
          : []),
        { label: "Loan Collateral", value: formData.loan_colleteral },
        ...(formData.loan_colleteral === "YES"
          ? [{ label: "Collateral Type", value: formData.collecteral_types }]
          : []),
        { label: "Other Charges", value: formData.other_charges },
        { label: "Loan Approval", value: formData.loan_approval },
        {
          label: "Priority Rule",
          value: Array.isArray(formData.priority_rule)
            ? formData.priority_rule.join(", ")
            : formData.priority_rule,
        },
      ],
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Please review the product details before creating.
      </Typography>
      <Grid container spacing={3}>
        {sections.map((section, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper elevation={3} sx={{ p: 2, height: "100%" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary"
                >
                  {section.title}
                </Typography>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setActiveStep(section.stepIndex)}
                >
                  Edit
                </Button>
              </Box>
              <Divider />
              <List dense>
                {section.fields.map((field, idx) => (
                  <ListItem key={idx} disablePadding>
                    <ListItemText
                      primary={field.label}
                      secondary={
                        field.value !== undefined &&
                        field.value !== null &&
                        field.value !== ""
                          ? field.value.toString()
                          : "-"
                      }
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: "medium",
                      }}
                      secondaryTypographyProps={{ variant: "body2" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Step7_Confirmation;
